/// <reference types="vite/client" />
/**
 * The one place an HTTP response becomes either a value or an `ApiError`.
 *
 * The backend answers every failure in a uniform envelope — `{detail, code}` — and echoes an
 * `X-Request-Id` on the way out. Normalising that **once, here** is what lets every screen above
 * branch on the semantic `code` and never on prose: `code === 'invalid_credentials'` survives a
 * copy edit, `detail === 'Email or password is incorrect.'` does not.
 *
 * FastAPI's own 422 is the one shape that differs — `detail` arrives as a list of field errors —
 * so it is unpacked into `fieldErrors`, keyed by the field path a form can actually use.
 *
 * **The base URL stays empty in dev.** The browser calls relative `/v1` paths, the Vite proxy
 * forwards them same-origin, and CORS never enters the picture. Setting `VITE_API_URL` locally
 * makes the browser call the API host directly and sign-in fails with an error that looks like
 * anything but its cause (`admin/CLAUDE.md`).
 */

export const API_BASE = import.meta.env.VITE_API_URL ?? '';

export interface FieldError {
  /** Dotted path into the request body — `body.email`, or `body.scopes.0`. */
  field: string;
  message: string;
}

/**
 * Everything the console knows about a failed call. `code` is what you branch on; `detail` is what
 * you show; `requestId` is what support quotes when they ask the API session what happened.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string;
  readonly requestId: string | null;
  readonly fieldErrors: readonly FieldError[];

  constructor(init: {
    status: number;
    code: string;
    detail: string;
    requestId?: string | null;
    fieldErrors?: readonly FieldError[];
  }) {
    super(init.detail);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.detail = init.detail;
    this.requestId = init.requestId ?? null;
    this.fieldErrors = init.fieldErrors ?? [];
  }

  /** True when the caller should stop and re-authenticate rather than retry or explain. */
  get isAuth(): boolean {
    return this.status === 401;
  }
}

/** The network never answered — a different thing from the server answering with a failure. */
export const OFFLINE_CODE = 'network_unreachable';

interface RawValidationError {
  loc?: unknown;
  msg?: unknown;
}

function fieldErrorsFrom(detail: readonly RawValidationError[]): FieldError[] {
  return detail.map((e) => ({
    field: Array.isArray(e.loc) ? e.loc.join('.') : '',
    message: typeof e.msg === 'string' ? e.msg : 'Invalid value',
  }));
}

/** One summary line for a 422, because a form also needs something to say at the top. */
function summarise(fieldErrors: readonly FieldError[]): string {
  if (fieldErrors.length === 0) return 'The server rejected this request.';
  if (fieldErrors.length === 1) return fieldErrors[0]!.message;
  return `${fieldErrors.length} fields need attention.`;
}

function toApiError(status: number, requestId: string | null, body: unknown): ApiError {
  const envelope = (typeof body === 'object' && body !== null ? body : {}) as {
    detail?: unknown;
    code?: unknown;
  };

  if (Array.isArray(envelope.detail)) {
    const fieldErrors = fieldErrorsFrom(envelope.detail as RawValidationError[]);
    return new ApiError({
      status,
      code: typeof envelope.code === 'string' ? envelope.code : 'validation_error',
      detail: summarise(fieldErrors),
      requestId,
      fieldErrors,
    });
  }

  return new ApiError({
    status,
    /* A response with no `code` is a bug in the backend or a proxy answering in its place. Say
       that rather than inventing a plausible one — `unknown_error` is greppable. */
    code: typeof envelope.code === 'string' ? envelope.code : 'unknown_error',
    detail: typeof envelope.detail === 'string' ? envelope.detail : `The server answered ${status}.`,
    requestId,
  });
}

/**
 * The sentence to show a person. `ApiError` already carries the server's own words; anything else
 * reaching a catch block is a bug in this client rather than an answer from the API, and saying so
 * vaguely beats rendering `[object Object]`.
 */
export function errorMessage(error: unknown): string {
  return error instanceof ApiError ? error.detail : 'Something went wrong. Try again.';
}

/** The code `deps.require_step_up` answers with when the session's sign-in is too old. */
export const STEP_UP_CODE = 'step_up_required';
/** Dispatched on `window` when an answer carries `STEP_UP_CODE`; the shell raises the lock. */
export const STEP_UP_EVENT = 'bookmypandiit:step-up';
/**
 * Dispatched on `window` when a success carries `Idempotency-Replayed: true` — the server answered
 * with the first attempt's result and ran nothing. The toast host says so (doc 03 §6.8), so an
 * operator who clicked twice does not go looking for a second batch.
 */
export const REPLAYED_EVENT = 'bookmypandiit:idempotency-replayed';

export interface ApiRequest {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** Serialised as JSON. Omit for GET and DELETE. */
  body?: unknown;
  /** Merged over the defaults — this is where a bearer token or an idempotency key arrives. */
  headers?: Record<string, string>;
  signal?: AbortSignal;
  /**
   * How to read a successful body. `json` is right for every endpoint but the exports.
   *
   * The tax return export is `text/csv` — a file a CA files from — and parsing it as JSON threw
   * `malformed_response` on a response that was perfectly correct. An error body is still JSON
   * whatever this says, because the error envelope is the same everywhere.
   */
  parse?: 'json' | 'text';
}

/**
 * Calls the API and returns the parsed body, or throws `ApiError`.
 *
 * `T` is supplied by the caller from the **generated** schema types. Never hand-write a response
 * type to make a call compile — that error is the contract telling you something moved.
 */
export async function apiFetch<T>(path: string, request: ApiRequest = {}): Promise<T> {
  const { method = 'GET', body, headers = {}, signal, parse = 'json' } = request;

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      signal,
      headers: {
        Accept: 'application/json',
        ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
        ...headers,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch (cause) {
    /* An abort is the caller's own doing — a navigation, a superseded query — and must not be
       dressed up as a server failure. */
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError({
      status: 0,
      code: OFFLINE_CODE,
      detail: 'The API did not answer. Check your connection, or whether the stack is running.',
    });
  }

  const requestId = response.headers.get('X-Request-Id');

  /* 204 and friends carry no body; parsing one would throw on an empty string. */
  if (response.status === 204) return undefined as T;

  let payload: unknown = null;
  const text = await response.text();
  /* A successful non-JSON body is returned as it came. Only the success path takes this branch:
     a failure still carries the standard `{detail, code}` envelope and is parsed below. */
  if (parse === 'text' && response.ok) return text as T;
  if (text.length > 0) {
    try {
      payload = JSON.parse(text);
    } catch {
      /* HTML from a proxy, or a truncated body. Either way the response is not the API's. */
      if (response.ok) {
        throw new ApiError({
          status: response.status,
          code: 'malformed_response',
          detail: 'The API returned something that is not JSON.',
          requestId,
        });
      }
    }
  }

  if (!response.ok) {
    const error = toApiError(response.status, requestId, payload);
    /* The server decides an action needs a fresh sign-in (sibling-learnings A5); the shell answers
       by raising the lock, whose unlock is that sign-in. The caller still gets the error, so the
       dialog that asked says why nothing happened. */
    if (error.code === STEP_UP_CODE) window.dispatchEvent(new Event(STEP_UP_EVENT));
    throw error;
  }
  if (response.headers.get('Idempotency-Replayed') === 'true') window.dispatchEvent(new Event(REPLAYED_EVENT));
  return payload as T;
}
